import React from 'react';

// Logo Option 1: AI Neural Network with Pulse
export const LogoNeural = ({ size = 40, animate = true }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        <linearGradient id="logoGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3B82F6" />
          <stop offset="50%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>
        <filter id="glow1">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Background Circle */}
      <circle cx="50" cy="50" r="45" fill="url(#logoGrad1)" />
      
      {/* Neural Network Nodes */}
      <g fill="white" filter="url(#glow1)">
        {/* Center Node */}
        <circle cx="50" cy="50" r="8" className={animate ? "animate-pulse" : ""} />
        
        {/* Outer Nodes */}
        <circle cx="25" cy="30" r="5" />
        <circle cx="75" cy="30" r="5" />
        <circle cx="25" cy="70" r="5" />
        <circle cx="75" cy="70" r="5" />
        <circle cx="50" cy="20" r="5" />
        <circle cx="50" cy="80" r="5" />
      </g>
      
      {/* Connection Lines */}
      <g stroke="white" strokeWidth="2" opacity="0.6">
        <line x1="50" y1="50" x2="25" y2="30" />
        <line x1="50" y1="50" x2="75" y2="30" />
        <line x1="50" y1="50" x2="25" y2="70" />
        <line x1="50" y1="50" x2="75" y2="70" />
        <line x1="50" y1="50" x2="50" y2="20" />
        <line x1="50" y1="50" x2="50" y2="80" />
      </g>
      
      {/* Pulse Ring */}
      {animate && (
        <circle 
          cx="50" cy="50" r="35" 
          fill="none" 
          stroke="white" 
          strokeWidth="2" 
          opacity="0.4"
          className="animate-ping"
          style={{ animationDuration: '2s' }}
        />
      )}
    </svg>
  </div>
);

// Logo Option 2: Clean Barcode POS Logo - Matches POS & Cash Register icon exactly
// Simple vertical bars, no animation, meant to be merged with text
export const LogoBrain = ({ size = 40 }) => (
  <div 
    className="relative flex items-center justify-center"
    style={{ width: size, height: size }}
  >
    <svg viewBox="0 0 24 24" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 8v8" />
      <path d="M12 8v8" />
      <path d="M17 8v8" />
    </svg>
  </div>
);

// Combined Logo + Text Component - Green box with white barcode icon + gradient text
export const FlowFeedLogo = ({ size = "default", showTagline = true, className = "" }) => {
  const sizes = {
    small: { icon: 38, text: "text-xl", tagline: "text-[9px]", radius: "rounded-lg" },
    default: { icon: 44, text: "text-2xl", tagline: "text-[10px]", radius: "rounded-xl" },
    large: { icon: 56, text: "text-3xl", tagline: "text-[11px]", radius: "rounded-xl" }
  };
  const s = sizes[size] || sizes.default;
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Green box with white barcode icon */}
      <div 
        className={`bg-gradient-to-br from-emerald-500 to-teal-600 ${s.radius} flex items-center justify-center shadow-lg shadow-emerald-500/25`}
        style={{ width: s.icon, height: s.icon }}
      >
        <svg viewBox="0 0 24 24" className="w-[65%] h-[65%] text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7V5a2 2 0 0 1 2-2h2" />
          <path d="M17 3h2a2 2 0 0 1 2 2v2" />
          <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
          <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
          <path d="M7 8v8" />
          <path d="M12 8v8" />
          <path d="M17 8v8" />
        </svg>
      </div>
      
      {/* Text with gradient styling */}
      <div className="flex flex-col">
        <span className={`${s.text} font-black tracking-tight leading-none bg-gradient-to-r from-slate-800 via-emerald-600 to-teal-500 dark:from-white dark:via-emerald-400 dark:to-teal-400 bg-clip-text text-transparent`}>
          FlowFeed
        </span>
        {showTagline && (
          <span className={`${s.tagline} font-semibold bg-gradient-to-r from-slate-500 to-emerald-600 dark:from-slate-400 dark:to-emerald-400 bg-clip-text text-transparent tracking-wide`}>
            AI-Powered POS
          </span>
        )}
      </div>
    </div>
  );
};

// White version for dark backgrounds (login page left panel)
export const FlowFeedLogoWhite = ({ size = "default", showTagline = true, className = "" }) => {
  const sizes = {
    small: { icon: 38, text: "text-xl", tagline: "text-[9px]", radius: "rounded-lg" },
    default: { icon: 44, text: "text-2xl", tagline: "text-[10px]", radius: "rounded-xl" },
    large: { icon: 56, text: "text-3xl", tagline: "text-[11px]", radius: "rounded-xl" }
  };
  const s = sizes[size] || sizes.default;
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Green box with white barcode icon */}
      <div 
        className={`bg-gradient-to-br from-emerald-500 to-teal-600 ${s.radius} flex items-center justify-center shadow-lg shadow-emerald-500/30`}
        style={{ width: s.icon, height: s.icon }}
      >
        <svg viewBox="0 0 24 24" className="w-[65%] h-[65%] text-white" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7V5a2 2 0 0 1 2-2h2" />
          <path d="M17 3h2a2 2 0 0 1 2 2v2" />
          <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
          <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
          <path d="M7 8v8" />
          <path d="M12 8v8" />
          <path d="M17 8v8" />
        </svg>
      </div>
      
      {/* Text with gradient styling for dark backgrounds */}
      <div className="flex flex-col">
        <span className={`${s.text} font-black tracking-tight leading-none bg-gradient-to-r from-white via-emerald-300 to-teal-300 bg-clip-text text-transparent`}>
          FlowFeed
        </span>
        {showTagline && (
          <span className={`${s.tagline} font-semibold bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent tracking-wide`}>
            AI-Powered POS
          </span>
        )}
      </div>
    </div>
  );
};

// Logo Option 3: Data Flow / Connectivity Waves
export const LogoDataFlow = ({ size = 40, animate = true }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        <linearGradient id="logoGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0EA5E9" />
          <stop offset="50%" stopColor="#6366F1" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
      
      <circle cx="50" cy="50" r="45" fill="url(#logoGrad3)" />
      
      {/* Wave Lines */}
      <g fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
        <path d="M25 50 Q35 35 50 50 Q65 65 75 50" className={animate ? "animate-pulse" : ""} />
        <path d="M25 40 Q35 25 50 40 Q65 55 75 40" opacity="0.6" />
        <path d="M25 60 Q35 45 50 60 Q65 75 75 60" opacity="0.6" />
      </g>
      
      {/* Center Core */}
      <circle cx="50" cy="50" r="10" fill="white" className={animate ? "animate-pulse" : ""} />
      <circle cx="50" cy="50" r="5" fill="url(#logoGrad3)" />
      
      {/* Orbiting Dots */}
      <g fill="white" opacity="0.8">
        <circle cx="25" cy="50" r="4" />
        <circle cx="75" cy="50" r="4" />
      </g>
    </svg>
  </div>
);

// Logo Option 4: AI Hexagon Tech
export const LogoHexTech = ({ size = 40, animate = true }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        <linearGradient id="logoGrad4" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#14B8A6" />
          <stop offset="50%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
      
      {/* Hexagon Background */}
      <polygon 
        points="50,5 90,27.5 90,72.5 50,95 10,72.5 10,27.5" 
        fill="url(#logoGrad4)"
      />
      
      {/* Inner Hexagon */}
      <polygon 
        points="50,20 75,35 75,65 50,80 25,65 25,35" 
        fill="none"
        stroke="white"
        strokeWidth="2"
        opacity="0.5"
      />
      
      {/* AI Symbol */}
      <text 
        x="50" y="58" 
        textAnchor="middle" 
        fill="white" 
        fontSize="28" 
        fontWeight="bold"
        fontFamily="monospace"
        className={animate ? "animate-pulse" : ""}
      >
        AI
      </text>
      
      {/* Corner Dots */}
      <g fill="white" opacity="0.7">
        <circle cx="50" cy="20" r="3" />
        <circle cx="75" cy="35" r="3" />
        <circle cx="75" cy="65" r="3" />
        <circle cx="50" cy="80" r="3" />
        <circle cx="25" cy="65" r="3" />
        <circle cx="25" cy="35" r="3" />
      </g>
    </svg>
  </div>
);

// Logo Option 5: Pulse Signal / Heartbeat
export const LogoPulse = ({ size = 40, animate = true }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        <linearGradient id="logoGrad5" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F43F5E" />
          <stop offset="50%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#3B82F6" />
        </linearGradient>
      </defs>
      
      <circle cx="50" cy="50" r="45" fill="url(#logoGrad5)" />
      
      {/* Pulse Line */}
      <path 
        d="M15 50 L30 50 L35 35 L45 65 L55 35 L65 65 L70 50 L85 50"
        fill="none"
        stroke="white"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={animate ? "animate-pulse" : ""}
      />
      
      {/* Outer Rings */}
      {animate && (
        <>
          <circle cx="50" cy="50" r="38" fill="none" stroke="white" strokeWidth="1" opacity="0.3" className="animate-ping" style={{ animationDuration: '2s' }} />
          <circle cx="50" cy="50" r="42" fill="none" stroke="white" strokeWidth="1" opacity="0.2" className="animate-ping" style={{ animationDuration: '2.5s' }} />
        </>
      )}
    </svg>
  </div>
);

// Logo Option 6: Connected Nodes / Network Hub
export const LogoHub = ({ size = 40, animate = true }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        <linearGradient id="logoGrad6" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F59E0B" />
          <stop offset="50%" stopColor="#EF4444" />
          <stop offset="100%" stopColor="#EC4899" />
        </linearGradient>
        <filter id="glow6">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      <rect x="5" y="5" width="90" height="90" rx="18" fill="url(#logoGrad6)" />
      
      {/* Connection Lines */}
      <g stroke="white" strokeWidth="2" opacity="0.5">
        <line x1="50" y1="50" x2="25" y2="25" />
        <line x1="50" y1="50" x2="75" y2="25" />
        <line x1="50" y1="50" x2="25" y2="75" />
        <line x1="50" y1="50" x2="75" y2="75" />
        <line x1="50" y1="50" x2="50" y2="20" />
        <line x1="50" y1="50" x2="50" y2="80" />
        <line x1="50" y1="50" x2="20" y2="50" />
        <line x1="50" y1="50" x2="80" y2="50" />
      </g>
      
      {/* Outer Nodes */}
      <g fill="white" filter="url(#glow6)">
        <circle cx="25" cy="25" r="6" />
        <circle cx="75" cy="25" r="6" />
        <circle cx="25" cy="75" r="6" />
        <circle cx="75" cy="75" r="6" />
        <circle cx="50" cy="20" r="5" />
        <circle cx="50" cy="80" r="5" />
        <circle cx="20" cy="50" r="5" />
        <circle cx="80" cy="50" r="5" />
      </g>
      
      {/* Center Hub */}
      <circle cx="50" cy="50" r="12" fill="white" className={animate ? "animate-pulse" : ""} />
      <circle cx="50" cy="50" r="6" fill="url(#logoGrad6)" />
    </svg>
  </div>
);

// Default export with all options
const Logo = {
  Neural: LogoNeural,
  Brain: LogoBrain,
  DataFlow: LogoDataFlow,
  HexTech: LogoHexTech,
  Pulse: LogoPulse,
  Hub: LogoHub
};

export default Logo;
